import React, { useEffect, useMemo, useState, forwardRef, useRef, } from "react";
import MUIDataTable from "mui-datatables";
import { Icon } from "@iconify/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DefaultAvatar from "../otherImages/default.png";
import Swal from "sweetalert2";

const PackageDataTable = () => {
  const [startDate, setStartDate] = useState(new Date("2025-01-01"));
  const [endDate, setEndDate] = useState(new Date("2026-12-31"));
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [previewVisible, setPreviewVisible] = useState(false);

  const previewContainerRef = useRef(null);

  const API_URL = "https://clearshine.koderspedia.online/get-bookings";

  useEffect(() => {
    getBookings();
  }, []);

  const getBookings = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const token = localStorage.getItem("token");

      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const result = await response.json();

      if (result?.success) {
        setBookings(result?.data || []);
      } else {
        setBookings([]);
        setErrorMessage(result?.message || "Failed to fetch bookings");
      }
    } catch (error) {
      console.error("Booking API Error:", error);
      setBookings([]);
      setErrorMessage("Something went wrong while fetching bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleViewSummary = (booking) => {
    setSelectedBooking(booking);
    setShowSummaryModal(true);
  };

  const handleCloseSummaryModal = () => {
    setSelectedBooking(null);
    setShowSummaryModal(false);
    setPreviewImage(null);
    setImageZoom(1);
    setPreviewVisible(false);
  };

  const handleOpenPreview = (imageUrl) => {
    if (!imageUrl || imageUrl === "url") return;

    setPreviewImage(imageUrl);
    setImageZoom(1);

    setTimeout(() => {
      setPreviewVisible(true);
    }, 20);
  };

  const handleClosePreview = () => {
    setPreviewVisible(false);

    setTimeout(() => {
      setPreviewImage(null);
      setImageZoom(1);
    }, 180);
  };

  const handleZoomIn = () => {
    setImageZoom((prev) => Math.min(prev + 0.2, 4));
  };

  const handleZoomOut = () => {
    setImageZoom((prev) => Math.max(prev - 0.2, 0.5));
  };

  const handleResetZoom = () => {
    setImageZoom(1);
  };

  const handleToggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        if (previewContainerRef.current?.requestFullscreen) {
          await previewContainerRef.current.requestFullscreen();
        }
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
    }
  };

  const formatCreatedAt = (dateValue) => {
    if (!dateValue) return "-";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleDeleteBooking = async (booking) => {
    if (!booking?.id) {
      Swal.fire({
        icon: "error",
        title: "Booking ID Missing",
        text: "Booking ID not found.",
        confirmButtonColor: "#0CAFE6",
      });
      return;
    }

    const confirmResult = await Swal.fire({
      title: "Delete Booking?",
      text: `Are you sure you want to delete booking #${booking.id}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!confirmResult.isConfirmed) return;

    try {
      Swal.fire({
        title: "Deleting...",
        text: "Please wait while booking is being deleted.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const token = localStorage.getItem("token");

      const response = await fetch(
        `https://clearshine.koderspedia.online/bookingListDelete/${booking.id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      const result = await response.json();
      console.log("Delete Booking Response:", result);

      if (response.ok && result?.success) {
        setBookings((prev) => prev.filter((item) => item.id !== booking.id));

        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: result?.message || "Booking deleted successfully.",
          confirmButtonColor: "#0CAFE6",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Delete Failed",
          text: result?.message || "Failed to delete booking.",
          confirmButtonColor: "#0CAFE6",
        });
      }
    } catch (error) {
      console.error("Delete Booking Error:", error);

      Swal.fire({
        icon: "error",
        title: "Something Went Wrong",
        text: "Something went wrong while deleting booking.",
        confirmButtonColor: "#0CAFE6",
      });
    }
  };

  const CustomInput = forwardRef(({ value, onClick }, ref) => (
    <button
      ref={ref}
      type="button"
      className="btn btn-outline-secondary d-flex align-items-center gap-2"
      onClick={onClick}
    >
      <Icon icon="uil:calender" width="18" />
      <span>{value}</span>
    </button>
  ));

  const filteredData = useMemo(() => {
    return bookings.filter((booking) => {
      const createdDate = new Date(booking.createdAt);

      const isInDateRange =
        createdDate >= new Date(new Date(startDate).setHours(0, 0, 0, 0)) &&
        createdDate <= new Date(new Date(endDate).setHours(23, 59, 59, 999));

      const isInServiceFilter = !filter || booking.service === filter;

      return isInDateRange && isInServiceFilter;
    });
  }, [bookings, startDate, endDate, filter]);

  const uniqueServices = useMemo(() => {
    const services = bookings.map((booking) => booking.service).filter(Boolean);

    return [...new Set(services)];
  }, [bookings]);

  const formatTimeWithAmPm = (timeValue) => {
    if (!timeValue) return "-";

    const [hours, minutes] = String(timeValue).split(":");

    if (hours === undefined || minutes === undefined) {
      return timeValue;
    }

    const hourNumber = Number(hours);

    if (Number.isNaN(hourNumber)) {
      return timeValue;
    }

    const ampm = hourNumber >= 12 ? "PM" : "AM";
    const formattedHour = hourNumber % 12 || 12;

    return `${formattedHour}:${minutes} ${ampm}`;
  };

  const previewTopIconStyle = {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(12, 18, 32, 0.72)",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    transition: "all 0.2s ease",
    boxShadow: "0 8px 24px rgba(0,0,0,0.22)",
    padding: 0,
  };

  const previewTopBarStyle = {
    position: "absolute",
    top: "12px",
    right: "12px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    zIndex: 10001,
  };

  const columns = [
    {
      name: "name",
      label: "Name",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const rowData = filteredData[dataIndex];

          return (
            <div className="col-packageName d-flex align-items-center gap-8">
              <img
                style={{
                  height: "35px",
                  width: "35px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
                src={DefaultAvatar}
                alt="booking"
              />
              <span>{rowData?.name || "-"}</span>
            </div>
          );
        },
      },
    },
    {
      name: "email",
      label: "Email",
      options: {
        customBodyRender: (value) => (
          <span className="col-email text-gray-600">{value || "-"}</span>
        ),
      },
    },
    {
      name: "service",
      label: "Service",
      options: {
        customBodyRender: (value) => (
          <span className="col-service px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {value || "-"}
          </span>
        ),
      },
    },
    {
      name: "date",
      label: "Date",
      options: {
        customBodyRender: (value) => (
          <span className="col-date text-gray-600">{value || "-"}</span>
        ),
      },
    },
    {
      name: "time",
      label: "Time",
      options: {
        customBodyRender: (value) => (
          <span className="col-time text-gray-600">
            {formatTimeWithAmPm(value)}
          </span>
        ),
      },
    },
    {
      name: "unit",
      label: "Unit",
      options: {
        customBodyRender: (value) => (
          <span className="col-unit text-gray-600">{value || "-"}</span>
        ),
      },
    },
    {
      name: "totalPrice",
      label: "Total Price",
      options: {
        customBodyRender: (value) => (
          <span className="col-price font-bold">
            ${Number(value || 0).toFixed(2)}
          </span>
        ),
      },
    },
    {
      name: "createdAt",
      label: "Created At",
      options: {
        customBodyRender: (value) => (
          <span className="col-createdAt text-gray-600">
            {formatCreatedAt(value)}
          </span>
        ),
      },
    },
    {
      name: "Manage",
      label: "Manage",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const rowData = filteredData[dataIndex];

          return (
            <div className="d-flex align-items-center gap-2">
              <Icon
                onClick={() => handleViewSummary(rowData)}
                className="editBtn hover: cursor-pointer"
                icon="solar:eye-outline"
                width="24"
                height="24"
              />

              <Icon
                onClick={() => handleDeleteBooking(rowData)}
                className="deleteBtn hover: cursor-pointer"
                icon="material-symbols:delete-outline"
                width="24"
                height="24"
              />
            </div>
          );
        },
      },
    },
  ];

  const options = {
    selectableRows: "none",
    rowsPerPage: 10,
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    textLabels: {
      body: {
        noMatch: loading
          ? "Loading bookings..."
          : errorMessage || "No bookings found",
      },
    },
  };

  return (
    <>
      <div className="card basic-data-table">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              customInput={<CustomInput />}
              dateFormat="dd MMM yyyy"
            />

            <span>-</span>

            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              customInput={<CustomInput />}
              dateFormat="dd MMM yyyy"
            />
          </div>

          <div className="filter-select">
            <select
              className="form-select"
              value={filter}
              id="serviceFilter"
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="">All Services</option>
              {uniqueServices.map((service) => (
                <option key={service} value={service}>
                  Service {service}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="card-body">
          <MUIDataTable
            data={filteredData}
            columns={columns}
            options={options}
            className="overflow-hidden packageTable"
          />
        </div>
      </div>

      {showSummaryModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Booking Summary</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseSummaryModal}
                ></button>
              </div>

              <div className="modal-body">
                <div className="mb-4">
                  <h6 className="mb-2">Customer Details</h6>

                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <strong>Name:</strong> {selectedBooking?.name || "-"}
                    </div>

                    <div className="col-md-6 mb-2">
                      <strong>Email:</strong> {selectedBooking?.email || "-"}
                    </div>

                    <div className="col-md-6 mb-2">
                      <strong>Service:</strong>{" "}
                      {selectedBooking?.service || "-"}
                    </div>

                    <div className="col-md-6 mb-2">
                      <strong>Date:</strong> {selectedBooking?.date || "-"}
                    </div>

                    <div className="col-md-6 mb-2">
                      <strong>Time:</strong>{" "}
                      {formatTimeWithAmPm(selectedBooking?.time)}
                    </div>

                    <div className="col-md-6 mb-2">
                      <strong>Unit:</strong> {selectedBooking?.unit || "-"}
                    </div>

                    <div className="col-md-6 mb-2">
                      <strong>Total Price:</strong> $
                      {Number(selectedBooking?.totalPrice || 0).toFixed(2)}
                    </div>

                    <div className="col-md-6 mb-2">
                      <strong>Created At:</strong>{" "}
                      {formatCreatedAt(selectedBooking?.createdAt)}
                    </div>
                  </div>
                </div>

                <h6 className="mb-3">Summary</h6>

                {selectedBooking?.summary?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-bordered align-middle">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Width</th>
                          <th>Height</th>
                          <th>Sqft</th>
                          <th>Amount</th>
                          <th>Image</th>
                        </tr>
                      </thead>

                      <tbody>
                        {selectedBooking.summary.map((item) => (
                          <tr key={item.id}>
                            <td>{item.id || "-"}</td>
                            <td>{item.width || "-"}</td>
                            <td>{item.height || "-"}</td>
                            <td>{item.sqft || "-"}</td>
                            <td>{item.amount || "-"}</td>
                            <td>
                              {item.image && item.image !== "url" ? (
                                <img
                                  src={item.image}
                                  alt="summary"
                                  onClick={() => handleOpenPreview(item.image)}
                                  title="Click to preview"
                                  style={{
                                    width: "70px",
                                    height: "70px",
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                    cursor: "zoom-in",
                                  }}
                                />
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="mb-0">No summary found.</p>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseSummaryModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.72)",
            backdropFilter: "blur(5px)",
            WebkitBackdropFilter: "blur(5px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            opacity: previewVisible ? 1 : 0,
            transition: "opacity 0.18s ease",
          }}
          onClick={handleClosePreview}
        >
          <div
            ref={previewContainerRef}
            style={{
              position: "relative",
              width: "min(96vw, 1200px)",
              height: "min(92vh, 820px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: previewVisible ? "scale(1)" : "scale(0.96)",
              transition: "transform 0.2s ease",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={previewTopBarStyle}>
              <button
                type="button"
                onClick={handleZoomOut}
                style={previewTopIconStyle}
                title="Zoom Out"
              >
                <Icon icon="mdi:magnify-minus-outline" width="20" />
              </button>

              <button
                type="button"
                onClick={handleResetZoom}
                style={previewTopIconStyle}
                title="Reset"
              >
                <Icon icon="mdi:restart" width="20" />
              </button>

              <button
                type="button"
                onClick={handleZoomIn}
                style={previewTopIconStyle}
                title="Zoom In"
              >
                <Icon icon="mdi:magnify-plus-outline" width="20" />
              </button>

              <button
                type="button"
                onClick={handleToggleFullscreen}
                style={previewTopIconStyle}
                title="Fullscreen"
              >
                <Icon icon="mdi:fullscreen" width="21" />
              </button>

              <button
                type="button"
                onClick={handleClosePreview}
                style={previewTopIconStyle}
                title="Close"
              >
                <Icon icon="mdi:close" width="22" />
              </button>
            </div>

            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <img
                src={previewImage}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  transform: `scale(${imageZoom})`,
                  transition: "transform 0.25s ease, opacity 0.2s ease",
                  transformOrigin: "center center",
                  display: "block",
                  userSelect: "none",
                  borderRadius: "10px",
                  opacity: previewVisible ? 1 : 0,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PackageDataTable;